import React, { useState, useRef, useEffect } from "react";
import "../../ticket/ticketListInfo/AuditTrail.scss";

// src/components/ticket/ticketListInfo/AuditTrail.scss
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import LeadTrailActionMenu from "./LeadTrailActionMenu";

interface IProps {
  leadTrailData: {
    forward_count: number;
    last_forward_date: string | null;
    audit_trail: any[];
  };
}

const Trail: React.FC<IProps> = ({ leadTrailData }) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Extract audit trail data safely
  const auditTrail = leadTrailData?.audit_trail ?? [];

  const toggleMenu = (index: number) => {
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const downloadCSV = () => {
    if (auditTrail.length === 0) return;

    const headers = [
      "Date",
      "Time",
      "Lead Activity",
      "Status",
      "Assignee",
      "Assigned By",
    ];

    const quoteCSV = (text: string) => {
      if (typeof text !== "string") text = String(text ?? "");
      if (/[,"\s]/.test(text)) {
        return '"' + text.replace(/"/g, '""') + '"';
      }
      return text;
    };

    const csvContent = [
      headers.map(quoteCSV).join(","),
      ...auditTrail.map((row) =>
        [
          quoteCSV(formatDate(row.created)),
          quoteCSV(formatTime(row.created)),
          quoteCSV(row.lead_activity || "N/A"),
          quoteCSV(row.new_status_type || "N/A"),
          quoteCSV(row.new_assignee || "N/A"),
          quoteCSV(row.assigned_by || "N/A"),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "lead_trail_data.csv");
  };

  const downloadExcel = () => {
    if (auditTrail.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(
      auditTrail.map((row) => ({
        Date: formatDate(row.created),
        Time: formatTime(row.created),
        LeadActivity: row.lead_activity || "N/A",
        Status: row.new_status_type || "N/A",
        Assignee: row.new_assignee || "N/A",
        AssignedBy: row.assigned_by || "N/A",
      })),
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lead Trail");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "lead_trail_data.xlsx");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get icon and badge type based on activity
  const getActivityType = (activity: string) => {
    const activityLower = activity?.toLowerCase() || "";

    console.log(activityLower, "activityLower");

    if (
      activityLower.includes("created") ||
      activityLower.includes("initialized")
    ) {
      return {
        icon: "bi-plus-circle-fill",
        iconClass: "atl-icon-created",
        badge: "atl-badge-created",
        badgeText: "CREATED",
      };
    } else if (activityLower.includes("external note")) {
      return {
        icon: "bi-chat-right-text-fill",
        iconClass: "atl-icon-note-external",
        badge: "atl-badge-updated",
        badgeText: "EXTERNAL NOTE",
      };
    } else if (activityLower.includes("internal note")) {
      return {
        icon: "bi-chat-left-text-fill",
        iconClass: "atl-icon-note-internal",
        badge: "atl-badge-updated",
        badgeText: "INTERNAL NOTE",
      };
    } else if (activityLower.includes("assignee")) {
      return {
        icon: "bi-person-fill",
        iconClass: "atl-icon-assigned",
        badge: "atl-badge-assigned",
        badgeText: "ASSIGNED",
      };
    } else if (activityLower.includes("status")) {
      return {
        icon: "bi-arrow-repeat",
        iconClass: "atl-icon-status",
        badge: "atl-badge-updated",
        badgeText: "STATUS CHANGED",
      };
    } else if (activityLower.includes("attachment")) {
      return {
        icon: "bi-paperclip",
        iconClass: "atl-icon-attachment",
        badge: "atl-badge-attachment",
        badgeText: "ATTACHMENT",
      };
    } else if (activityLower.includes("information updated")) {
      return {
        icon: "bi-pencil-square",
        iconClass: "atl-icon-info",
        badge: "atl-badge-updated",
        badgeText: "INFO UPDATED",
      };
    } else if (activityLower.includes("email")) {
      return {
        icon: "bi-envelope-fill",
        iconClass: "atl-icon-email",
        badge: "atl-badge-updated",
        badgeText: "EMAIL",
      };
    } else if (activityLower.includes("comment")) {
      return {
        icon: "bi-chat-fill",
        iconClass: "atl-icon-comment",
        badge: "atl-badge-updated",
        badgeText: "COMMENT",
      };
    } else if (
      activityLower.includes("deleted") ||
      activityLower.includes("removed")
    ) {
      return {
        icon: "bi-trash-fill",
        iconClass: "atl-icon-deleted",
        badge: "atl-badge-deleted",
        badgeText: "DELETED",
      };
    } else if (activityLower.includes("child ticket")) {
      return {
        icon: "bi-diagram-3-fill",
        iconClass: "atl-icon-child-ticket",
        badge: "atl-badge-created",
        badgeText: "CHILD TICKET",
      };
    } else if (
      activityLower.includes("update") ||
      activityLower.includes("change") ||
      activityLower.includes("priority")
    ) {
      return {
        icon: "bi-arrow-repeat",
        iconClass: "atl-icon-update",
        badge: "atl-badge-updated",
        badgeText: "UPDATED",
      };
    } else {
      return {
        icon: "bi-circle-fill",
        iconClass: "atl-icon-default",
        badge: "atl-badge-updated",
        badgeText: "ACTIVITY",
      };
    }
  };

  //   const getActivityType = (activity: string, status: string) => {
  //   const activityLower = activity?.toLowerCase() || "";
  //   const statusLower = status?.toLowerCase() || "";

  //   if (activityLower.includes("created") || activityLower.includes("initialized")) {
  //     return {
  //       icon: "bi-plus-circle-fill",
  //       iconClass: "atl-icon-created",
  //       badge: "atl-badge-created",
  //       badgeText: "CREATED",
  //     };
  //   } else if (activityLower.includes("assigned") || activityLower.includes("assignee")) {
  //     return {
  //       icon: "bi-person-fill",
  //       iconClass: "atl-icon-assigned",
  //       badge: "atl-badge-assigned",
  //       badgeText: "ASSIGNED",
  //     };
  //   } else if (activityLower.includes("status") || statusLower) {
  //     return {
  //       icon: "bi-arrow-repeat",
  //       iconClass: "atl-icon-status",
  //       badge: "atl-badge-updated",
  //       badgeText: "STATUS CHANGED",
  //     };
  //   } else if (activityLower.includes("forwarded")) {
  //     return {
  //       icon: "bi-arrow-right-circle-fill",
  //       iconClass: "atl-icon-update",
  //       badge: "atl-badge-updated",
  //       badgeText: "FORWARDED",
  //     };
  //   } else if (activityLower.includes("update") || activityLower.includes("change")) {
  //     return {
  //       icon: "bi-arrow-repeat",
  //       iconClass: "atl-icon-update",
  //       badge: "atl-badge-updated",
  //       badgeText: "UPDATED",
  //     };
  //   } else {
  //     return {
  //       icon: "bi-circle-fill",
  //       iconClass: "atl-icon-default",
  //       badge: "atl-badge-updated",
  //       badgeText: "ACTIVITY",
  //     };
  //   }
  // };

  // Get tooltip text
  const getTooltipText = (activity: string) => {
    const activityLower = activity?.toLowerCase() || "";

    if (activityLower.includes("created")) return "Lead Created";
    if (activityLower.includes("assigned")) return "Lead Assigned";
    if (activityLower.includes("status")) return "Status Updated";
    if (activityLower.includes("forwarded")) return "Lead Forwarded";
    return "Activity";
  };

  // Get user avatar initials
  const getUserInitials = (name: string) => {
    if (!name || name === "N/A") return "S";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 1).toUpperCase();
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string, index: number) => {
    const colors = [
      "atl-avatar-green",
      "atl-avatar-blue",
      "atl-avatar-purple",
      "atl-avatar-gray",
    ];
    if (!name || name === "N/A") return "atl-avatar-gray";
    return colors[name.length % colors.length];
  };

  // Check if row should be highlighted (forwarded)
  const isForwardedRow = (rowDate: string, rowTime: string) => {
    if (!leadTrailData.last_forward_date) return false;
    const forwardedDate = formatDate(leadTrailData.last_forward_date);
    const forwardedTime = formatTime(leadTrailData.last_forward_date);
    return rowDate === forwardedDate && rowTime === forwardedTime;
  };

  return (
    <div className="audit-trail-timeline-wrapper">
      <div className="audit-trail-timeline-header">
        <h2 className="audit-trail-timeline-title">Lead Trail</h2>
        <div className="audit-trail-export-actions">
          <button className="audit-trail-export-button" onClick={downloadCSV}>
            <i className="bi bi-file-earmark-text text-white"></i>
            Export CSV
          </button>
          <button className="audit-trail-export-button" onClick={downloadExcel}>
            <i className="bi bi-file-earmark-spreadsheet text-white"></i>
            Export Excel
          </button>
        </div>
      </div>

      {auditTrail && auditTrail.length > 0 ? (
        <div className="audit-trail-timeline-items-container">
          {auditTrail.map((data, index) => {
            const activityType = getActivityType(data.lead_activity);
            const formattedDate = formatDate(data.created);
            const formattedTime = formatTime(data.created);
            const isHighlighted = isForwardedRow(formattedDate, formattedTime);

            return (
              <div
                key={index}
                className={`audit-trail-timeline-event ${
                  isHighlighted ? "atl-event-highlighted" : ""
                }`}
              >
                <div
                  className={`audit-trail-timeline-event-icon ${activityType.iconClass}`}
                  title={getTooltipText(data.lead_activity)}
                  data-tooltip={getTooltipText(data.lead_activity)}
                >
                  <i className={activityType.icon}></i>
                </div>

                <div className="audit-trail-timeline-event-card">
                  <div className="audit-trail-event-card-header">
                    <div className="audit-trail-event-timestamp">
                      {formattedDate} • {formattedTime}
                    </div>
                    <div
                      className={`audit-trail-event-status-badge ${activityType.badge}`}
                    >
                      {activityType.badgeText}
                    </div>
                  </div>

                  <div className="audit-trail-event-card-body">
                    {data.lead_activity && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          {activityType.badgeText === "ASSIGNED"
                            ? "ACTION"
                            : activityType.badgeText === "CREATED"
                            ? "EVENT"
                            : "ACTIVITY"}
                        </div>
                        <div className="audit-trail-field-value">
                          {data.lead_activity}
                        </div>
                      </div>
                    )}

                    {data.new_status_type && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">STATUS</div>
                        <div className="audit-trail-field-value">
                          {data.new_status_type}
                        </div>
                      </div>
                    )}

                    {data.new_assignee && data.new_assignee !== "N/A" && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">ASSIGNEE</div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.new_assignee,
                              index,
                            )}`}
                          >
                            {getUserInitials(data.new_assignee)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.new_assignee}
                          </div>
                        </div>
                      </div>
                    )}

                    {data.assigned_by && data.assigned_by !== "N/A" && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          {activityType.badgeText === "ASSIGNED"
                            ? "ASSIGNED BY"
                            : activityType.badgeText === "CREATED"
                            ? "LOGGED BY"
                            : "MODIFIED BY"}
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.assigned_by,
                              index + 1,
                            )}`}
                          >
                            {getUserInitials(data.assigned_by)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.assigned_by}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="audit-trail-timeline-no-data">
          <div className="audit-trail-empty-icon">
            <i className="bi bi-clock-history"></i>
          </div>
          <div className="audit-trail-empty-heading">No Lead Trail Data</div>
          <div className="audit-trail-empty-description">
            There are no updates to display for this lead.
          </div>
        </div>
      )}
    </div>
  );
};

export default Trail;
